/**
 * EL CUADRO PARA QUIEN NO TIENE CUENTA.
 *
 * QUÉ PROBLEMA RESUELVE. El cuadro de un torneo se comparte por WhatsApp, y
 * del otro lado lo abre gente que no está en la aplicación: familiares, el
 * club rival, el que todavía no se registró. Con la vista de adentro, ese
 * enlace llevaba a la pantalla de ingreso — y quien la ve no piensa «me falta
 * cuenta», piensa que el enlace estaba roto.
 *
 * NO ES `BracketFullPage` CON UN PERMISO MENOS. Es otra pantalla que consume
 * OTRO endpoint, `GET /public/tournaments/:id`, que devuelve otro tipo. Si
 * fuera la misma pantalla con un `if`, el día que alguien agregue un dato a
 * la vista de adentro tendría que acordarse de que existe un camino donde ese
 * dato no puede salir. Acá no hay nada que acordarse: el dato no llega.
 *
 * LO QUE SE VE ES EL APODO Y LA CATEGORÍA. El nombre legal, el teléfono y la
 * foto de perfil no salen de la aplicación. No se ocultan en el dibujo: no
 * viajan. Ver `PublicService`.
 *
 * NO SE ANOTA NADA. Ni marcadores, ni fechas, ni fotos: no hay quién los
 * firme. Por eso `Bracket` va sin `onScore` y sin `onSchedule`.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import type { PublicMatch, PublicTournament } from '@tennisup/contracts';
import { categoryKey } from '@tennisup/contracts';

import { Alert } from '../components/Alert';
import { Bracket, byRound } from '../components/Bracket';
import {
  BracketSearch,
  llevarAlPartido,
  partidoMásAvanzado,
} from '../components/BracketSearch';
import { Logo } from '../components/Logo';
import { NextMatches, nombreDeRonda, rondaEnJuego } from '../components/NextMatches';
import { SponsorCarousel } from '../components/SponsorCarousel';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';
import { categoryName } from '../lib/categoryName';

export function PublicBracketPage(): JSX.Element {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const [torneo, setTorneo] = useState<PublicTournament | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [destacado, setDestacado] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setTorneo(await api.publicTournament(id));
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Ver el mismo efecto en `BracketFullPage`: se mueve después de dibujar.
  useEffect(() => {
    llevarAlPartido(partidoMásAvanzado(torneo?.matches ?? [], destacado));
  }, [torneo, destacado]);

  return (
    <div className="full-bracket">
      {/*
        LA BARRA NO LLEVA «VOLVER». Quien llega acá desde WhatsApp no tiene a
        dónde volver dentro de la aplicación: el botón lo devolvería a una
        pantalla que no puede ver. Lo que sí lleva es el nombre del club, que
        es lo que ubica el torneo para alguien de afuera, y una invitación a
        entrar — que es la única razón para que esta pantalla exista además de
        informar.
      */}
      <header className="full-bracket-bar">
        <div className="public-title">
          <h1>{torneo?.name ?? t('bracket.title')}</h1>
          {torneo !== null && <p className="help">{torneo.communityName}</p>}
        </div>
        {torneo !== null && (
          <span className={`tag tag-${torneo.status.toLowerCase()}`}>
            {t(`tournamentStatus.${torneo.status}`)}
          </span>
        )}
      </header>

      <div className="full-bracket-body">
        {error !== null && <Alert kind="error">{error}</Alert>}

        {/* Ver el mismo bloque en `BracketFullPage`: los patrocinadores van
            antes que nada, y acá con más razón — este es el enlace que se
            manda por WhatsApp y el que abre gente de afuera del club.
            Va fuera del `if` de las categorías: un torneo sin ningún cuadro
            tirado todavía tiene patrocinadores que mostrar. */}
        {torneo !== null && <SponsorCarousel sponsors={torneo.sponsors} />}

        {torneo === null ? (
          error === null ? (
            <p className="help">{t('common.loading')}</p>
          ) : null
        ) : torneo.categories.length === 0 ? (
          <p className="help">{t('bracket.noneStarted')}</p>
        ) : (
          <>
            <BracketSearch
              matches={torneo.matches}
              destacado={destacado}
              onPick={setDestacado}
            />

            {torneo.categories.map((category) => {
              const propios = torneo.matches.filter(
                (m) => categoryKey(m) === categoryKey(category),
              );
              return (
                <section key={categoryKey(category)}>
                  <h2>
                    {categoryName(t, category)}
                  </h2>
                  <EnQueVa matches={propios} />
                  <Bracket
                    matches={propios}
                    tournamentName={torneo.name}
                    destacado={destacado}
                    deadlines={torneo.roundDeadlines.filter(
                      (d) => categoryKey(d) === categoryKey(category),
                    )}
                  />
                </section>
              );
            })}

            <NextMatches matches={torneo.matches} deadlines={torneo.roundDeadlines} />
          </>
        )}

        {/*
          LA ÚNICA PUERTA HACIA ADENTRO.

          Era un enlace de texto suelto al pie, y se leía como un pie de
          página: nadie aprieta un pie de página. Acá la marca dice de qué
          aplicación estamos hablando —quien llegó por un enlace de WhatsApp
          no lo sabe—, una línea dice qué gana entrando, y el botón es un
          botón. Los tres juntos, porque el logo solo es decoración, la frase
          sola es publicidad y el botón solo no dice a dónde lleva.

          El logo va en `full`: acá hay lugar y sobra: es lo único que hay que
          mirar en esa parte de la pantalla.
        */}
        <section className="public-invite">
          <Logo size={64} variant="full" alt={t('app.name')} />
          <p className="public-invite-line">{t('bracket.inviteBlurb')}</p>
          <Link className="button-like" to="/">
            {t('bracket.openApp')}
          </Link>
        </section>
      </div>
    </div>
  );
}

/** En qué ronda va esta categoría. Ver el mismo componente en `BracketFullPage`. */
function EnQueVa({ matches }: { matches: readonly PublicMatch[] }): JSX.Element | null {
  const { t } = useTranslation();
  const ronda = rondaEnJuego(matches);
  const columnas = byRound(matches);
  if (columnas.length === 0) return null;

  if (ronda === null) {
    return <p className="help full-bracket-round">{t('tournamentStatus.FINISHED')}</p>;
  }
  return (
    <p className="help full-bracket-round">
      {t('bracket.playing', { round: nombreDeRonda(t, 2 ** columnas.length, ronda) })}
    </p>
  );
}
